import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Card,
  TextField,
  Box,
  Grid,
  Input,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  Paper,
  IconButton,
  Switch,
  FormControlLabel,
  Input as MuiInput,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Link } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';

const API_URL = window.location.hostname === 'localhost'
  ? "http://localhost:5400/admin"
  : "https://crmb.apitrading.lk/admin";

// Restored original CustomInput styles
const CustomInput = styled(MuiInput)(({ theme }) => ({
  '&:before': {
    borderBottom: '1px solid #444', // Default underline color
  },
  '&:hover:not(.Mui-disabled):before': {
    borderBottom: '1.5px solid #e52e71', // Hover underline color
  },
  '&:after': {
    borderBottom: '1.5px solid #F15A24', // Focus underline color
    boxShadow: `0 0 5px #e52e71`, // Add shadow on focus
  },
}));

function CarQuotation({ userRole, userId, userName }) {
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [models, setModels] = useState([]);
  const [categoryNames, setCategoryNames] = useState([]);

  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedCategoryName, setSelectedCategoryName] = useState("");
  const [selectedPowerUnit, setSelectedPowerUnit] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedColorCode, setSelectedColorCode] = useState("");

  // Manual input fields
  const [manualBrand, setManualBrand] = useState("");
  const [manualCategory, setManualCategory] = useState("");
  const [manualModel, setManualModel] = useState("");
  const [manualYear, setManualYear] = useState("");
  const [manualGrade, setManualGrade] = useState("");
  const [manualColor, setManualColor] = useState("");

  const [cifValueJPY, setCifValueJPY] = useState("");
  const [cifValueLKR, setCifValueLKR] = useState("");
  const [customDutiesJPY, setCustomDutiesJPY] = useState("");
  const [customDutiesLKR, setCustomDutiesLKR] = useState("");
  const [clearanceChargesJPY, setClearanceChargesJPY] = useState("");
  const [clearanceChargesLKR, setClearanceChargesLKR] = useState("");
  const [additionalChargesJPY, setAdditionalChargesJPY] = useState("");
  const [additionalChargesLKR, setAdditionalChargesLKR] = useState("");
  const [options, setOptions] = useState([]);
  const [clientInfo, setClientInfo] = useState({
    username: "",
    email: "",
    telephone: "",
    address: "",
  });
  const [total, setTotal] = useState(0);
  const [netTotal, setNetTotal] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [exchangeRate, setExchangeRate] = useState("");
  const [open, setOpen] = useState(false);
  const [newRate, setNewRate] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [specialNotes, setSpecialNotes] = useState("");
  const [useDropdown, setUseDropdown] = useState(true);

  useEffect(() => {
    if (useDropdown) {
      fetch(`${API_URL}/brands`)
        .then((res) => res.json())
        .then(setBrands);
    }
  }, [useDropdown]);

  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleSelection = useCallback(async (type, value) => {
    if (type === "category" && !selectedBrand) {
      showSnackbar("Please select a Brand first!");
      return;
    }
    if (type === "model" && !selectedCategory) {
      showSnackbar("Please select a Category first!");
      return;
    }
    if (type === "categoryName" && !selectedModel) {
      showSnackbar("Please select a Model first!");
      return;
    }

    resetSelections(type);

    if (type === "brand") {
      setSelectedBrand(value);
      fetchData(`/categories/${value}`, setCategories);
    } else if (type === "category") {
      setSelectedCategory(value);
      fetchData(`/models/${selectedBrand}/${value}`, setModels);
    } else if (type === "model") {
      const selectedModelObj = models.find((m) => m.Name === value);
      if (selectedModelObj) {
        setSelectedModel(selectedModelObj);
        fetchData(`/category-name/${selectedModelObj.id}`, setCategoryNames);
      }
    } else if (type === "categoryName") {
      setSelectedCategoryName(value);
    }
  }, [selectedBrand, selectedCategory, selectedModel, models]);

  const resetSelections = (level) => {
    switch (level) {
      case "brand":
        setCategories([]);
        setModels([]);
        setCategoryNames([]);
        setSelectedCategory("");
        setSelectedModel(null);
        setSelectedYear("");
        setSelectedCategoryName("");
        setSelectedColor("");
        setSelectedColorCode("");
        break;
      case "category":
        setModels([]);
        setCategoryNames([]);
        setSelectedModel(null);
        setSelectedYear("");
        setSelectedCategoryName("");
        setSelectedColor("");
        setSelectedColorCode("");
        break;
      case "model":
        setCategoryNames([]);
        setSelectedYear("");
        setSelectedCategoryName("");
        setSelectedColor("");
        setSelectedColorCode("");
        break;
      case "categoryName":
        setSelectedColor("");
        setSelectedColorCode("");
        break;
      default:
        break;
    }
  };

  const fetchData = async (url, setter) => {
    fetch(`${API_URL}${url}`)
      .then((res) => res.json())
      .then(setter)
      .catch((err) => console.error("Error fetching data:", err));
  };

  const areDropdownsFilled = () => {
    return selectedBrand && selectedCategory && selectedModel && selectedCategoryName;
  };

  useEffect(() => {
    const calculatedTotal = (
      parseFloat(cifValueLKR || 0) +
      parseFloat(customDutiesLKR || 0) +
      parseFloat(clearanceChargesLKR || 0) +
      parseFloat(additionalChargesLKR || 0)
    );
    const optionsSum = options.reduce((sum, opt) => sum + parseFloat(opt.priceLKR || 0), 0);
    const calculatedNetTotal = calculatedTotal + optionsSum;

    setTotal(isNaN(calculatedTotal) ? 0 : calculatedTotal);
    setNetTotal(isNaN(calculatedNetTotal) ? 0 : calculatedNetTotal);
  }, [cifValueLKR, customDutiesLKR, clearanceChargesLKR, additionalChargesLKR, options]);

  const handleAddOption = () => {
    setOptions([...options, { name: "", priceJPY: "", priceLKR: "" }]);
  };

  const handleRemoveOption = (index) => {
    const newOptions = options.filter((_, i) => i !== index);
    setOptions(newOptions);
  };

  const handleSpecialNotesChange = useCallback((e) => {
    setSpecialNotes(e.target.value);
  }, []);

  const handleClientInfoChange = useCallback((e) => {
    const { name, value } = e.target;
    setClientInfo((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleGeneratePDF = async () => {
    const vehicleDetails = useDropdown
      ? {
          modelId: selectedModel?.id || "",
          brand: selectedBrand || selectedModel?.Brand || "",
          category: selectedCategory || "",
          model: selectedModel?.Name || "",
          year: selectedYear || "",
          categoryName: selectedCategoryName || "",
          color: selectedColor || "",
          colorCode: selectedColorCode || "",
          powerUnit: selectedPowerUnit || "",
        }
      : {
          modelId: "",
          brand: manualBrand || "",
          category: manualCategory || "",
          model: manualModel || "",
          year: manualYear || "",
          categoryName: manualGrade || "",
          color: manualColor || "",
          colorCode: "",
          powerUnit: "",
        };

    const quotationData = {
      vehicleDetails,
      cifValueJPY,
      cifValueLKR,
      customDutiesJPY,
      customDutiesLKR,
      clearanceChargesJPY,
      clearanceChargesLKR,
      additionalChargesJPY,
      additionalChargesLKR,
      options: options.map((option) => ({
        name: option.name || "",
        priceJPY: parseFloat(option.priceJPY) || 0,
        priceLKR: parseFloat(option.priceLKR) || 0,
      })),
      specialNotes,
      clientInfo,
      total,
      netTotal,
      adminId: userId,
      adminName: userName,
    };

    try {
      const response = await fetch(`${API_URL}/save-quotation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(quotationData),
        credentials: "include",
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to generate PDF: ${errorText}`);
      }
      const blob = await response.blob();
      setPdfBlob(blob);

      const pdfUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = pdfUrl;
      const dateTime = new Date().toLocaleString().replace(/[/:]/g, "_").replace(/ /g, "_");
      link.download = `${clientInfo.username}_${dateTime}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setShowButtons(true);
    } catch (error) {
      console.error("❌ Error generating PDF:", error);
      setSnackbarMessage("Failed to generate PDF. Please try again.");
      setSnackbarOpen(true);
    }
  };

  const handleSendEmail = () => {
    if (!pdfBlob) {
      console.error("PDF not available.");
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(pdfBlob);
    reader.onloadend = () => {
      const base64data = reader.result.split(",")[1];
      const htmlContent = `
        <html lang="en">
          <body>
            <div class="container">
              <h1 style="text-align: center; color: #e44d26; font-style: italic;">API <span style="color: gray;">Trading</span></h1>
              <h2>Hello ${clientInfo.username},</h2>
              <p>Thank you for requesting a quotation. Below are the details of your vehicle:</p>
              <h3>User Details:</h3>
              <ul>
                <li><strong>Name:</strong> ${clientInfo.username}</li>
                <li><strong>Email:</strong> ${clientInfo.email}</li>
                <li><strong>Phone:</strong> ${clientInfo.telephone}</li>
              </ul>
              <h3>Vehicle Details:</h3>
              <ul>
                <li><strong>Brand:</strong> ${selectedBrand || selectedModel?.Brand || ""}</li>
                <li><strong>Model:</strong> ${selectedModel?.Name || ""}</li>
                <li><strong>Year:</strong> ${selectedYear || ""}</li>
                <li><strong>Category:</strong> ${selectedCategory || ""}</li>
                <li><strong>Color:</strong></span> ${selectedColor || ""}</li>
                <li><strong>Power Unit:</strong> ${selectedPowerUnit || ""}</li>
              </ul>
              <p>Please find the attached PDF for the full quotation details.</p>
            </div>
          </body>
        </html>
      `;

      const emailData = {
        to: clientInfo.email,
        subject: "Vehicle Quotation",
        html: htmlContent,
        attachment: { filename: "quotation.pdf", content: base64data },
      };
      sendEmailWithAttachment(emailData);
    };
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const sendEmailWithAttachment = (emailData) => {
    fetch(`${API_URL}/send-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(emailData),
      credentials: "include",
    })
      .then((response) => {
        if (!response.ok) throw new Error("Failed to send email");
        return response.json();
      })
      .then(() => {
        setSnackbarMessage("Email sent successfully!");
        setSnackbarOpen(true);
      })
      .catch(() => {
        setSnackbarMessage("Failed to send email. Please try again.");
        setSnackbarOpen(true);
      });
  };

  const handleSendWhatsApp = async () => {
    if (!pdfBlob || !clientInfo.telephone) {
      setSnackbarMessage("Please generate a PDF and ensure client phone number is provided.");
      setSnackbarOpen(true);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(pdfBlob);
    reader.onloadend = async () => {
      const base64data = reader.result.split(",")[1];
      const message = `Hello ${clientInfo.username},\n\nHere is your vehicle quotation from API Trading! Please find the attached PDF document.`;

      try {
        const response = await fetch(`${API_URL}/send-whatsapp-pdf`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phoneNumber: clientInfo.telephone,
            message,
            pdfBase64: base64data,
            filename: `${clientInfo.username}_quotation_${new Date().toISOString().slice(0, 10)}.pdf`,
          }),
          credentials: "include",
        });

        if (!response.ok) throw new Error("Failed to send WhatsApp message");
        setSnackbarMessage("Quotation sent successfully via WhatsApp!");
        setSnackbarOpen(true);
      } catch (error) {
        setSnackbarMessage("Failed to send WhatsApp message. Please try again.");
        setSnackbarOpen(true);
      }
    };
  };

  const handleClick = () => {
    if (!clientInfo.username ||
      (useDropdown && (!selectedBrand || !selectedModel || !selectedYear)) ||
      (!useDropdown && (!manualBrand || !manualModel || !manualYear))) {
      setSnackbarMessage("Please fill in all required fields before clicking the button.");
      setOpenSnackbar(true);
    } else {
      handleOpenModal();
    }
  };

  useEffect(() => {
    fetch(`${API_URL}/exchange-rate`)
      .then((response) => response.json())
      .then((data) => setExchangeRate(data.rate || "0.0000"))
      .catch((error) => console.error("Error fetching exchange rate:", error));
  }, []);

  const handleClickOpen = () => {
    setNewRate(exchangeRate);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleSave = async () => {
    try {
      const response = await fetch(`${API_URL}/exchange-rate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rate: newRate }),
      });

      if (response.ok) {
        setExchangeRate(newRate);
        setOpen(false);
      }
    } catch (error) {
      console.error("Error updating exchange rate:", error);
    }
  };

  const handleOptionChange = useCallback((index, name, value) => {
    const newOptions = [...options];
    if (!newOptions[index]) newOptions[index] = { name: "", priceJPY: "", priceLKR: "" };

    if (name === "name") {
      newOptions[index].name = value;
    } else if (name === "JPY") {
      newOptions[index].priceJPY = value;
      newOptions[index].priceLKR = (parseFloat(value) * parseFloat(exchangeRate) || 0).toFixed(2);
    }

    setOptions(newOptions);
  }, [options, exchangeRate]);

  useEffect(() => {
    setOptions((prevOptions) =>
      prevOptions.map((option) => ({
        ...option,
        priceLKR: (parseFloat(option.priceJPY) * parseFloat(exchangeRate) || 0).toFixed(2),
      }))
    );
  }, [exchangeRate]);

  const handleCIFChange = useCallback((value) => {
    setCifValueJPY(value);
    setCifValueLKR((parseFloat(value) * parseFloat(exchangeRate) || 0).toFixed(2));
  }, [exchangeRate]);

  const handleCustomDutiesChange = useCallback((value) => {
    setCustomDutiesLKR(value);
  }, []);

  const handleClearanceChargesChange = useCallback((value) => {
    setClearanceChargesLKR(value);
  }, []);
  return (
    <Container maxWidth="xl" sx={{ py: 1 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0 }}>
        <Typography variant="h4" sx={{ textAlign: "center", fontWeight: "bold", color: "#F15A24", fontSize: { xs: '1.1rem', md: '2.125rem' } }}>
          ADD CAR QUOTATIONS
        </Typography>
        <Button
          component={Link}
          to={userRole === "superadmin" ? "/carsSuperAdmin/allquotations" : "/carsAdmin/allquotations"}
          variant="contained"
          sx={{
            background: "linear-gradient(45deg, #F15A24, #e52e71)",
            color: "white",
            px: 3,
            py: 1,
            fontSize: { xs: '0.6rem', md: '1rem' },
            fontWeight: "bold",
            borderRadius: "50px",
            boxShadow: "0 4px 10px rgba(241, 90, 36, 0.3)",
            "&:hover": {
              background: "linear-gradient(45deg, #e52e71, #F15A24)",
              boxShadow: "0 6px 15px rgba(241, 90, 36, 0.4)",
              transform: "translateY(-2px)",
              transition: "all 0.3s ease",
            },
          }}
        >
          All Quotations
        </Button>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 2 }}>
        <Paper
          sx={{
            display: "flex",
            alignItems: "center",
            padding: "1px 15px",
            borderRadius: "50px",
            backgroundColor: "white",
            border: "3px solid",
            borderLeftColor: "#e52e71",
            borderRightColor: "#F15A24",
            borderTopColor: "#666",
            borderBottomColor: "#666",
            color: "#444",
            cursor: "pointer",
            boxShadow: 5,
            '&:hover': {
              boxShadow: 10,
            },
          }}
          onClick={handleClickOpen}
        >
          <Typography
            variant="body1"
            sx={{
              color: "#333",
              fontWeight: "600",
              textShadow: "0 1px 1px rgba(0, 0, 0, 0.1)",
              fontSize: { xs: '0.8rem', md: '1rem' }
            }}
          >
            Currency Exchange Rate : (1 LKR = {exchangeRate})
          </Typography>
          <IconButton size="small" sx={{ ml: 1, color: "#e52e71" }}>
            <EditIcon />
          </IconButton>
        </Paper>
      </Box>

      <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: "bold", color: "#444", fontSize: { xs: '0.8rem', md: '1.25rem' } }}>
        SELECT VEHICLE DETAILS
      </Typography>
      <FormControlLabel
        control={
          <Switch
            checked={useDropdown}
            onChange={() => setUseDropdown(!useDropdown)}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: '#F15A24',
                '&:hover': {
                  backgroundColor: 'rgba(241, 90, 36, 0.08)',
                },
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: '#e52e71',
              },
              '& .MuiSwitch-track': {
                background: 'linear-gradient(45deg, #F15A24, #e52e71)',
              },
            }}
          />
        }
        label={useDropdown ? "Using Dropdowns" : "Using Manual Input"}
        sx={{
          mb: 2,
          '& .MuiTypography-root': {
            color: '#444',
            fontWeight: 'bold',
            fontSize: { xs: '0.8rem', md: '1rem' }
          }
        }}
      />

      {useDropdown ? (
        <Box
          sx={{
            display: "flex",
            gap: 3,
            flexWrap: "wrap",
            justifyContent: "space-between",
            mb: 1,
            p: 2,
            backgroundColor: "background.paper",
            borderRadius: 2,
            boxShadow: 1,
          }}
        >
          {[
            ["Brand", selectedBrand, brands || [], "brand"],
            ["Category", selectedCategory, categories || [], "category"],
            ["Model", selectedModel ? selectedModel.Name : "", models.map((m) => m.Name) || [], "model"],
            ["Grade", selectedCategoryName, categoryNames || [], "categoryName"],
          ].map(([label, selected, options, type]) => (
            <FormControl key={type} sx={{ minWidth: 180, flexGrow: 1 }}>
              <InputLabel sx={{ color: "#444", "&.Mui-focused": { color: "#e52e71" }, fontSize: { xs: '0.8rem', md: '1rem' } }}>{label}</InputLabel>
              <Select
                value={selected}
                onChange={(e) => handleSelection(type, e.target.value)}
                label={label}
                sx={{
                  backgroundColor: "background.paper",
                  height: "small",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#444",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e52e71",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e52e71",
                  },
                  fontSize: { xs: '0.8rem', md: '1rem' }
                }}
              >
                {(Array.isArray(options) ? options : []).map((option) => (
                  <MenuItem
                    key={typeof option === "object" ? option.id || option.Name : option}
                    value={typeof option === "object" ? option.Name : option}
                    sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }}
                  >
                    <Typography>{typeof option === "object" ? option.Name : option}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}

          <TextField
            label="Year"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            disabled={!areDropdownsFilled()}
            sx={{
              minWidth: 180,
              flexGrow: 1,
              "& .MuiOutlinedInput-root": {
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#e52e71" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#e52e71" },
                "&.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
              },
              "& .MuiInputLabel-root": {
                color: "#444",
                "&.Mui-focused": { color: "#e52e71" },
                fontSize: { xs: '0.8rem', md: '1rem' }
              },
              fontSize: { xs: '0.8rem', md: '1rem' }
            }}
            variant="outlined"
          />

          <TextField
            label="Color"
            value={selectedColor}
            onChange={(e) => setSelectedColor(e.target.value)}
            disabled={!areDropdownsFilled()}
            sx={{
              minWidth: 180,
              flexGrow: 1,
              "& .MuiOutlinedInput-root": {
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#e52e71" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#e52e71" },
                "&.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
              },
              "& .MuiInputLabel-root": {
                color: "#444",
                "&.Mui-focused": { color: "#e52e71" },
                fontSize: { xs: '0.8rem', md: '1rem' }
              },
              fontSize: { xs: '0.8rem', md: '1rem' }
            }}
            variant="outlined"
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            gap: 3,
            flexWrap: "wrap",
            justifyContent: "space-between",
            mb: 1,
            p: 2,
            backgroundColor: "background.paper",
            borderRadius: 2,
            boxShadow: 1,
          }}
        >
          <TextField
            label="Brand"
            value={manualBrand}
            onChange={(e) => setManualBrand(e.target.value)}
            sx={{
              minWidth: 180, flexGrow: 1,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#F15A24' },
                '&:hover fieldset': { borderColor: '#F15A24' },
                '&.Mui-focused fieldset': { borderColor: '#F15A24' },
              },
              '& .MuiInputLabel-root.Mui-focused': { color: '#F15A24' },
              fontSize: { xs: '0.8rem', md: '1rem' }
            }}
            variant="outlined"
          />
          <TextField
            label="Category"
            value={manualCategory}
            onChange={(e) => setManualCategory(e.target.value)}
            sx={{
              minWidth: 180, flexGrow: 1,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#F15A24' },
                '&:hover fieldset': { borderColor: '#F15A24' },
                '&.Mui-focused fieldset': { borderColor: '#F15A24' },
              },
              '& .MuiInputLabel-root.Mui-focused': { color: '#F15A24' },
              fontSize: { xs: '0.8rem', md: '1rem' }
            }}
            variant="outlined"
          />
          <TextField
            label="Model"
            value={manualModel}
            onChange={(e) => setManualModel(e.target.value)}
            sx={{
              minWidth: 180, flexGrow: 1,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#F15A24' },
                '&:hover fieldset': { borderColor: '#F15A24' },
                '&.Mui-focused fieldset': { borderColor: '#F15A24' },
              },
              '& .MuiInputLabel-root.Mui-focused': { color: '#F15A24' },
              fontSize: { xs: '0.8rem', md: '1rem' }
            }}
            variant="outlined"
          />
          <TextField
            label="Year"
            value={manualYear}
            onChange={(e) => setManualYear(e.target.value)}
            sx={{
              minWidth: 180, flexGrow: 1,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#F15A24' },
                '&:hover fieldset': { borderColor: '#F15A24' },
                '&.Mui-focused fieldset': { borderColor: '#F15A24' },
              },
              '& .MuiInputLabel-root.Mui-focused': { color: '#F15A24' },
              fontSize: { xs: '0.8rem', md: '1rem' }
            }}
            variant="outlined"
          />
          <TextField
            label="Grade"
            value={manualGrade}
            onChange={(e) => setManualGrade(e.target.value)}
            sx={{
              minWidth: 180, flexGrow: 1,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#F15A24' },
                '&:hover fieldset': { borderColor: '#F15A24' },
                '&.Mui-focused fieldset': { borderColor: '#F15A24' },
              },
              '& .MuiInputLabel-root.Mui-focused': { color: '#F15A24' },
              fontSize: { xs: '0.8rem', md: '1rem' }
            }}
            variant="outlined"
          />
          <TextField
            label="Color"
            value={manualColor}
            onChange={(e) => setManualColor(e.target.value)}
            sx={{
              minWidth: 180, flexGrow: 1,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#F15A24' },
                '&:hover fieldset': { borderColor: '#F15A24' },
                '&.Mui-focused fieldset': { borderColor: '#F15A24' },
              },
              '& .MuiInputLabel-root.Mui-focused': { color: '#F15A24' },
              fontSize: { xs: '0.8rem', md: '1rem' }
            }}
            variant="outlined"
          />
        </Box>
      )}

      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ p: 3, borderRadius: 2, boxShadow: 1 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold", color: "#444", fontSize: { xs: '1rem', md: '1.25rem' } }}>
              QUOTATION DETAILS
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ '&.Mui-focused': { color: '#444' }, fontSize: { xs: '0.8rem', md: '1rem' } }}>CIF Value (JPY)</InputLabel>
                  <CustomInput 
                    value={cifValueJPY} 
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      handleCIFChange(value);
                    }}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ '&.Mui-focused': { color: '#444' }, fontSize: { xs: '0.8rem', md: '1rem' } }}>CIF Value (LKR)</InputLabel>
                  <CustomInput value={cifValueLKR} disabled sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }} />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ '&.Mui-focused': { color: '#444' }, fontSize: { xs: '0.8rem', md: '1rem' } }}>Additional Charges (JPY)</InputLabel>
                  <CustomInput 
                    value={additionalChargesJPY} 
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setAdditionalChargesJPY(value);
                      setAdditionalChargesLKR((parseFloat(value) * parseFloat(exchangeRate) || 0).toFixed(2));
                    }}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ '&.Mui-focused': { color: '#444' }, fontSize: { xs: '0.8rem', md: '1rem' } }}>Additional Charges (LKR)</InputLabel>
                  <CustomInput value={additionalChargesLKR} disabled sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }} />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel sx={{ '&.Mui-focused': { color: '#444' }, fontSize: { xs: '0.8rem', md: '1rem' } }}>Custom Duties (LKR)</InputLabel>
                  <CustomInput 
                    value={customDutiesLKR} 
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      handleCustomDutiesChange(value);
                    }}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel sx={{ '&.Mui-focused': { color: '#444' }, fontSize: { xs: '0.8rem', md: '1rem' } }}>Clearance Charges (LKR)</InputLabel>
                  <CustomInput 
                    value={clearanceChargesLKR} 
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      handleClearanceChargesChange(value);
                    }}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }}
                  />
                </FormControl>
              </Grid>
            </Grid>

            <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: "bold", color: "#444", fontSize: { xs: '1rem', md: '1.25rem' } }}>
              OPTIONS
            </Typography>
            {options.map((option, index) => (
              <Box key={index} sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, mb: 2 }}>
                <FormControl fullWidth>
                  <InputLabel sx={{ '&.Mui-focused': { color: '#444' }, fontSize: { xs: '0.8rem', md: '1rem' } }}>Option Name</InputLabel>
                  <CustomInput value={option.name} onChange={(e) => handleOptionChange(index, "name", e.target.value)} sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }} />
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel sx={{ '&.Mui-focused': { color: '#444' }, fontSize: { xs: '0.8rem', md: '1rem' } }}>Price (JPY)</InputLabel>
                  <CustomInput value={option.priceJPY} onChange={(e) => handleOptionChange(index, "JPY", e.target.value)} sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }} />
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel sx={{ '&.Mui-focused': { color: '#444' }, fontSize: { xs: '0.8rem', md: '1rem' } }}>Price (LKR)</InputLabel>
                  <CustomInput value={option.priceLKR} disabled sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }} />
                </FormControl>
                <Button onClick={() => handleRemoveOption(index)} variant="outlined" color="error">
                  <RemoveCircleOutlineIcon />
                </Button>
              </Box>
            ))}
            <Button onClick={handleAddOption} variant="outlined" sx={{ mt: 2 }}>
              Add Option
            </Button>

            <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: "bold", color: "#444", fontSize: { xs: '1rem', md: '1.25rem' } }}>
              SPECIAL NOTE
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              placeholder="Enter any special notes here..."
              value={specialNotes}
              onChange={handleSpecialNotesChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: 'linear-gradient(45deg, rgba(255,255,255,0.9), rgba(255,255,255,0.8))',
                  '& fieldset': { borderImage: '#444 1', borderWidth: '1px', borderStyle: 'solid' },
                  '&:hover fieldset': { borderImage: 'linear-gradient(45deg, #e52e71, #F15A24) 1', borderWidth: '1.5px' },
                  '&.Mui-focused fieldset': { borderImage: 'linear-gradient(45deg, #F15A24, #e44d26) 1', borderWidth: '1.5px', boxShadow: '0 0 12px rgba(241, 90, 36, 0.4)' },
                },
                '& .MuiInputBase-input': { color: '#444', background: 'transparent', fontSize: { xs: '0.8rem', md: '1rem' } },
                '& .MuiInputLabel-root.Mui-focused': { color: '#444', fontSize: { xs: '0.8rem', md: '1rem' } }
              }}
            />
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ p: 3, borderRadius: 2, boxShadow: 1 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold", color: "#444", fontSize: { xs: '1rem', md: '1.25rem' } }}>
              CLIENT INFORMATION
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ '&.Mui-focused': { color: '#444' }, fontSize: { xs: '0.8rem', md: '1rem' } }}>Client name</InputLabel>
                  <CustomInput name="username" value={clientInfo.username} onChange={handleClientInfoChange} sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }} />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ '&.Mui-focused': { color: '#444' }, fontSize: { xs: '0.8rem', md: '1rem' } }}>Email</InputLabel>
                  <CustomInput name="email" value={clientInfo.email} onChange={handleClientInfoChange} sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }} />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel sx={{ '&.Mui-focused': { color: '#444' }, fontSize: { xs: '0.8rem', md: '1rem' } }}>Telephone</InputLabel>
                  <CustomInput name="telephone" value={clientInfo.telephone} onChange={handleClientInfoChange} sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }} />
                  
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel sx={{ '&.Mui-focused': { color: '#444' },fontSize: { xs: '0.8rem', md: '1rem' } }}>Address</InputLabel>
                  <CustomInput name="address" value={clientInfo.address} onChange={handleClientInfoChange} />
                </FormControl>
              </Grid>
              <Box
                sx={{
                  mt: { xs: 1, md: 2 },
                  mb: 0,
                  p: { xs: 1, md: 2 },
                  borderRadius: 2,
                  boxShadow: 3,
                  background: "linear-gradient(45deg, #ff8a00, #e52e71)",
                  color: "white",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: "bold", letterSpacing: 1, fontSize: { xs: '0.9rem', md: '1.25rem' } }}>
                  Total Amount
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: "bold", mt: 1, fontSize: { xs: '1rem', md: '1.5rem' } }}>
                  {total.toLocaleString()} LKR
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: "bold",
                    mt: { xs: 1, md: 2 },
                    px: { xs: 2, md: 3 },
                    py: { xs: 0.5, md: 1 },
                    borderRadius: 1,
                    backgroundColor: "rgba(255, 255, 255, 0.86)",
                    boxShadow: 1,
                    backdropFilter: "blur(5px)",
                    color: "rgb(240, 30, 2)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  Net Total
                  <Typography variant="h4" sx={{ fontWeight: "bold", mt: 1, fontSize: { xs: '1.2rem', md: '2rem' } }}>
                    {netTotal.toLocaleString()} LKR
                  </Typography>
                </Typography>
              </Box>
            </Grid>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "center" }}>
        <Button
          variant="contained"
          onClick={handleClick}
          sx={{
            background: "linear-gradient(45deg, #F15A24, #e52e71)",
            color: "white",
            px: { xs: 2, md: 4 },
            py: { xs: 1, md: 1.5 },
            fontSize: { xs: '0.7rem', md: '1.1rem' },
            fontWeight: "bold",
            boxShadow: "0 4px 10px rgba(241, 90, 36, 0.3)",
            "&:hover": {
              background: "linear-gradient(45deg, #e52e71, #F15A24)",
              boxShadow: "0 6px 15px rgba(241, 90, 36, 0.4)",
              transform: "translateY(-2px)",
              transition: "all 0.3s ease",
            },
          }}
        >
          Preview Quotation
        </Button>
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="error" sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarMessage.includes("successfully") ? "success" : "error"}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="md" fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            backgroundColor: 'rgba(255, 255, 255, 0.48)',
            backdropFilter: 'blur(25px)',
            WebkitBackdropFilter: 'blur(8px)',
          }
        }}>
        <IconButton
              onClick={() => setOpenModal(false)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: 'white',
                backgroundColor: 'rgba(255, 0, 0, 0.68)',
                borderRadius: '50%',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'rotate(90deg) scale(1.1)',
                  backgroundColor: 'rgba(255, 0, 0, 0.9)',
                  boxShadow: '0 0 8px rgba(255, 0, 0, 0.5)'
                }
              }}
            >
              <CloseIcon fontSize={'medium'} />
            </IconButton>
        <DialogTitle sx={{
          background: 'linear-gradient(45deg, rgb(241, 91, 36), rgb(229, 46, 113))',
          color: 'white',
          textAlign: 'center',
          fontSize: { xs: '1rem', md: '1.5rem' },
          fontWeight: 'bold',
          py: { xs: 0.5, md: 1 },
          lineHeight: 1.2
        }}>
          QUOTATION PREVIEW
        </DialogTitle>
        <DialogContent sx={{ background: 'transparent', pt: { xs: 1, md: 2 } }}>
          <Grid container spacing={3} sx={{ py: { xs: 1, md: 2 }, display: { xs: 'none', md: 'flex' } }}>
            <Grid item xs={12} sx={{ position: 'relative', mt: { xs: 1, md: 2 } }}>
              <Box sx={{
                background: 'linear-gradient(90deg, rgba(248, 221, 211, 0.93) 0%, rgba(238, 212, 199, 0.57) 100%)',
                p: { xs: 1, md: 2 },
                borderRadius: 2,
                borderLeft: '4px solid #F15A24'
              }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: { xs: 1, md: 2 }, display: 'flex', alignItems: 'center', gap: 1, color: '#333', fontSize: { xs: '1rem', md: '1.5rem' } }}>
                  <span style={{ fontSize: '1.2rem' }}>🚗</span> Vehicle Details
                </Typography>
                <Grid container spacing={2}>
                  {[
                    ['Brand', useDropdown ? selectedBrand : manualBrand],
                    ['Model', useDropdown ? (selectedModel?.Name || "") : manualModel],
                    ['Grade', useDropdown ? selectedCategoryName : manualGrade],
                    ['Year', useDropdown ? selectedYear : manualYear],
                    ['Color', useDropdown ? selectedColor : manualColor]
                  ].map(([label, value], index) => (
                    <Grid item xs={6} key={index}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', p: { xs: 0.5, md: 1 }, background: 'rgb(255, 255, 255)', borderRadius: 1, boxShadow: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#F15A24', fontSize: { xs: '0.8rem', md: '1rem' } }}>{label}:</Typography>
                        <Typography variant="body1" sx={{ color: '#555', fontSize: { xs: '0.8rem', md: '1rem' } }}>{value}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ display: { xs: 'block', md: 'none' }, py: { xs: 1, md: 2 } }}>
            <Box sx={{
              background: 'linear-gradient(90deg, rgba(248, 221, 211, 0.93) 0%, rgba(238, 212, 199, 0.57) 100%)',
              p: { xs: 1, md: 2 },
              borderRadius: 2,
              borderLeft: '4px solid #F15A24',
              mb: 2
            }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: { xs: 1, md: 2 }, display: 'flex', alignItems: 'center', gap: 1, color: '#333', fontSize: { xs: '1rem', md: '1.5rem' } }}>
                <span style={{ fontSize: '1.2rem' }}>🚗</span> Vehicle Details
              </Typography>
              {[
                ['Brand', useDropdown ? selectedBrand : manualBrand],
                ['Model', useDropdown ? (selectedModel?.Name || "") : manualModel],
                ['Grade', useDropdown ? selectedCategoryName : manualGrade],
                ['Year', useDropdown ? selectedYear : manualYear],
                ['Color', useDropdown ? selectedColor : manualColor]
              ].map(([label, value], index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center', p: { xs: 0.5, md: 1 }, background: 'rgb(255, 255, 255)', borderRadius: 1, boxShadow: 1, mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#F15A24', fontSize: { xs: '0.8rem', md: '1rem' } }}>{label}:</Typography>
                  <Typography variant="body1" sx={{ color: '#555', fontSize: { xs: '0.8rem', md: '1rem' } }}>{value}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Grid container spacing={3} sx={{ py: { xs: 1, md: 2 }, display: { xs: 'none', md: 'flex' } }}>
            <Grid item xs={12} sx={{ position: 'relative', mt: { xs: 1, md: 2 } }}>
              <Box sx={{
                background: 'linear-gradient(90deg, rgb(207, 241, 220) 0%, rgba(37,211,102,0.3) 100%)',
                p: { xs: 1, md: 2 },
                borderRadius: 2,
                borderLeft: '4px solid #25D366'
              }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: { xs: 1, md: 2 }, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1rem', md: '1.5rem' } }}>
                  <span style={{ fontSize: '1.2rem' }}>💰</span> Financial Details
                </Typography>
                <Grid container spacing={2}>
                  {[
                    ['CIF Value (JPY)', `${cifValueJPY} JPY`],
                    ['CIF Value (LKR)', `${cifValueLKR} LKR`],
                    ['Additional Charges (JPY)', `${additionalChargesJPY} JPY`],
                    ['Additional Charges (LKR)', `${additionalChargesLKR} LKR`],
                    ['Custom Duties (LKR)', `${customDutiesLKR} LKR`],
                    ['Clearance Charge (LKR)', `${clearanceChargesLKR} LKR`]
                  ].map(([label, value], index) => (
                    <Grid item xs={6} key={index}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', p: { xs: 0.5, md: 1 }, background: 'rgba(255,255,255,0.9)', borderRadius: 1, boxShadow: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#25D366', fontSize: { xs: '0.8rem', md: '1rem' } }}>{label}:</Typography>
                        <Typography variant="body1" sx={{ color: '#555', fontSize: { xs: '0.8rem', md: '1rem' } }}>{value}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ display: { xs: 'block', md: 'none' }, py: { xs: 1, md: 2 } }}>
            <Box sx={{
              background: 'linear-gradient(90deg, rgb(207, 241, 220) 0%, rgba(37,211,102,0.3) 100%)',
              p: { xs: 1, md: 2 },
              borderRadius: 2,
              borderLeft: '4px solid #25D366',
              mb: 2
            }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: { xs: 1, md: 2 }, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1rem', md: '1.5rem' } }}>
                <span style={{ fontSize: '1.2rem' }}>💰</span> Financial Details
              </Typography>
              {[
                ['CIF Value (JPY)', `${cifValueJPY} JPY`],
                ['CIF Value (LKR)', `${cifValueLKR} LKR`],
                ['Additional Charges (JPY)', `${additionalChargesJPY} JPY`],
                ['Additional Charges (LKR)', `${additionalChargesLKR} LKR`],
                ['Custom Duties (LKR)', `${customDutiesLKR} LKR`],
                ['Clearance Charge (LKR)', `${clearanceChargesLKR} LKR`]
              ].map(([label, value], index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center', p: { xs: 0.5, md: 1 }, background: 'rgba(255,255,255,0.9)', borderRadius: 1, boxShadow: 1, mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#25D366', fontSize: { xs: '0.8rem', md: '1rem' } }}>{label}:</Typography>
                  <Typography variant="body1" sx={{ color: '#555', fontSize: { xs: '0.8rem', md: '1rem' } }}>{value}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Grid container spacing={3} sx={{ py: { xs: 1, md: 2 }, display: { xs: 'none', md: 'flex' } }}>
            <Grid item xs={12} sx={{ position: 'relative', mt: { xs: 1, md: 2 } }}>
              <Box sx={{
                background: 'linear-gradient(90deg, rgb(214, 227, 240) 0%, rgba(0,123,255,0.3) 100%)',
                p: { xs: 1, md: 2 },
                borderRadius: 2,
                borderLeft: '4px solid #007bff'
              }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: { xs: 1, md: 2 }, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1rem', md: '1.5rem' } }}>
                  <span style={{ fontSize: '1.2rem' }}>👤</span> Client Information
                </Typography>
                <Grid container spacing={2}>
                  {[
                    ['Name', clientInfo.username],
                    ['Email', clientInfo.email],
                    ['Telephone', clientInfo.telephone],
                    ['Address', clientInfo.address]
                  ].map(([label, value], index) => (
                    <Grid item xs={6} key={index}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', p: { xs: 0.5, md: 1 }, background: 'rgba(255,255,255,0.9)', borderRadius: 1, boxShadow: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#007bff', fontSize: { xs: '0.8rem', md: '1rem' } }}>{label}:</Typography>
                        <Typography variant="body1" sx={{ color: '#555', fontSize: { xs: '0.8rem', md: '1rem' } }}>{value}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ display: { xs: 'block', md: 'none' }, py: { xs: 1, md: 2 } }}>
            <Box sx={{
              background: 'linear-gradient(90deg, rgb(214, 227, 240) 0%, rgba(0,123,255,0.3) 100%)',
              p: { xs: 1, md: 2 },
              borderRadius: 2,
              borderLeft: '4px solid #007bff',
              mb: 2
            }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: { xs: 1, md: 2 }, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1rem', md: '1.5rem' } }}>
                <span style={{ fontSize: '1.2rem' }}>👤</span> Client Information
              </Typography>
              {[
                ['Name', clientInfo.username],
                ['Email', clientInfo.email],
                ['Telephone', clientInfo.telephone],
                ['Address', clientInfo.address]
              ].map(([label, value], index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center', p: { xs: 0.5, md: 1 }, background: 'rgba(255,255,255,0.9)', borderRadius: 1, boxShadow: 1, mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#007bff', fontSize: { xs: '0.8rem', md: '1rem' } }}>{label}:</Typography>
                  <Typography variant="body1" sx={{ color: '#555', fontSize: { xs: '0.8rem', md: '1rem' } }}>{value}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Grid container spacing={3} sx={{ py: { xs: 1, md: 2 }, display: { xs: 'none', md: 'flex' } }}>
            <Grid item xs={12} sx={{ position: 'relative', mt: { xs: 1, md: 2 } }}>
              <Box sx={{
                background: 'linear-gradient(90deg, rgb(218, 209, 235) 0%, rgba(153,102,255,0.3) 100%)',
                p: { xs: 1, md: 2 },
                borderRadius: 2,
                borderLeft: '4px solid #9966ff'
              }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: { xs: 1, md: 2 }, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1rem', md: '1.5rem' } }}>
                  <span style={{ fontSize: '1.2rem' }}>⚙️</span> Additional Options
                </Typography>
                <Grid container spacing={2}>
                  {options.map((option, index) => (
                    <Grid item xs={12} key={index} sx={{ pl: { xs: 1, md: 3 } }}>
                      <Box sx={{ p: { xs: 1, md: 2 }, background: 'rgba(255,255,255,0.9)', borderRadius: 2, boxShadow: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#9966ff', fontSize: { xs: '0.8rem', md: '1rem' } }}>{option.name}</Typography>
                        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                          <Typography variant="body2" sx={{ color: '#F15A24', bgcolor: 'rgba(241,90,36,0.1)', px: { xs: 1, md: 1.5 }, py: { xs: 0.3, md: 0.5 }, borderRadius: 1, fontSize: { xs: '0.7rem', md: '0.9rem' } }}>
                            {option.priceJPY} JPY
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#25D366', bgcolor: 'rgba(37,211,102,0.1)', px: { xs: 1, md: 1.5 }, py: { xs: 0.3, md: 0.5 }, borderRadius: 1, fontSize: { xs: '0.7rem', md: '0.9rem' } }}>
                            {option.priceLKR} LKR
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ display: { xs: 'block', md: 'none' }, py: { xs: 1, md: 2 } }}>
            <Box sx={{
              background: 'linear-gradient(90deg, rgb(218, 209, 235) 0%, rgba(153,102,255,0.3) 100%)',
              p: { xs: 1, md: 2 },
              borderRadius: 2,
              borderLeft: '4px solid #9966ff',
              mb: 2
            }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: { xs: 1, md: 2 }, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1rem', md: '1.5rem' } }}>
                <span style={{ fontSize: '1.2rem' }}>⚙️</span> Additional Options
              </Typography>
              {options.map((option, index) => (
                <Box key={index} sx={{ p: { xs: 1, md: 2 }, background: 'rgba(255,255,255,0.9)', borderRadius: 2, boxShadow: 1, mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#9966ff', fontSize: { xs: '0.8rem', md: '1rem' } }}>{option.name}</Typography>
                  <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <Typography variant="body2" sx={{ color: '#F15A24', bgcolor: 'rgba(241,90,36,0.1)', px: { xs: 1, md: 1.5 }, py: { xs: 0.3, md: 0.5 }, borderRadius: 1, fontSize: { xs: '0.7rem', md: '0.9rem' } }}>
                      {option.priceJPY} JPY
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#25D366', bgcolor: 'rgba(37,211,102,0.1)', px: { xs: 1, md: 1.5 }, py: { xs: 0.3, md: 0.5 }, borderRadius: 1, fontSize: { xs: '0.7rem', md: '0.9rem' } }}>
                      {option.priceLKR} LKR
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          <Grid container spacing={3} sx={{ py: { xs: 1, md: 2 }, display: { xs: 'none', md: 'flex' } }}>
            <Grid item xs={12} sx={{ position: 'relative', mt: { xs: 1, md: 2 } }}>
              <Box sx={{
                background: 'linear-gradient(90deg, rgb(253, 237, 189) 0%, rgba(255,193,7,0.3) 100%)',
                p: { xs: 1, md: 2 },
                borderRadius: 2,
                borderLeft: '4px solid #ffc107'
              }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: { xs: 1, md: 2 }, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1rem', md: '1.5rem' } }}>
                  <span style={{ fontSize: '1.2rem' }}>📝</span> Special Notes
                </Typography>
                <Box sx={{ p: { xs: 1, md: 2 }, background: 'rgba(255,255,255,0.9)', borderRadius: 2, boxShadow: 1, fontStyle: 'italic', color: '#666', fontSize: { xs: '0.8rem', md: '1rem' } }}>
                  {specialNotes || "No special notes provided"}
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ display: { xs: 'block', md: 'none' }, py: { xs: 1, md: 2 } }}>
            <Box sx={{
              background: 'linear-gradient(90deg, rgb(253, 237, 189) 0%, rgba(255,193,7,0.3) 100%)',
              p: { xs: 1, md: 2 },
              borderRadius: 2,
              borderLeft: '4px solid #ffc107',
              mb: 2
            }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: { xs: 1, md: 2 }, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1rem', md: '1.5rem' } }}>
                <span style={{ fontSize: '1.2rem' }}>📝</span> Special Notes
              </Typography>
              <Box sx={{ p: { xs: 1, md: 2 }, background: 'rgba(255,255,255,0.9)', borderRadius: 2, boxShadow: 1, fontStyle: 'italic', color: '#666', fontSize: { xs: '0.8rem', md: '1rem' } }}>
                {specialNotes || "No special notes provided"}
              </Box>
            </Box>
          </Box>

          <Box sx={{ position: 'fixed', bottom: { xs: 50, md: 90 }, left: 0, right: 0, zIndex: 1000, background: 'linear-gradient(45deg, #F15A24, #e52e71)', boxShadow: 3 }}>
            <Grid container spacing={1} sx={{ height: { xs: 40, md: 50 }, alignItems: 'center' }}>
              <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', textShadow: '2px 2px 4px rgba(0,0,0,0.2)', lineHeight: 1.1, py: { xs: 0.2, md: 0.3 }, fontSize: { xs: '0.9rem', md: '1.1rem' } }}>
                  Total: {total.toLocaleString()} LKR
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', textShadow: '2px 2px 4px rgba(0,0,0,0.2)', lineHeight: 1.1, py: { xs: 0.2, md: 0.3 }, fontSize: { xs: '0.9rem', md: '1.1rem' } }}>
                  Net: {netTotal.toLocaleString()} LKR
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 1,
          p: { xs: 1, md: 2 },
          background: 'rgba(245, 245, 245, 0)',
          borderTop: '1px solid rgba(0, 0, 0, 0.08)',
        }}>
          <Button
            onClick={handleGeneratePDF}
            sx={{
              background: 'linear-gradient(145deg, #F15A24 30%, #e52e71 90%)',
              color: 'white',
              px: { xs: 3, md: 5 },
              py: { xs: 0.5, md: 1},
              fontSize: { xs: '0.9rem', md: '1.2rem' },
              borderRadius: '12px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
              textTransform: 'none',
              letterSpacing: '0px',
              minWidth: { xs: '120px', md: '150px' },
              '&:hover': {
                background: 'linear-gradient(145deg, #e52e71 30%, #F15A24 90%)',
                transform: 'scale(1.05)',
                boxShadow: '0 6px 8px rgba(0,0,0,0.3)',
              },
              '&:active': {
                transform: 'scale(0.95)',
              },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '@media (max-width: 600px)': {
                px: 2,
                py: 0.5,
                fontSize: '0.8rem',
                minWidth: '120px'
              }
            }}
          >
            Generate PDF
          </Button>
          {showButtons && (
            <Button
              onClick={handleSendEmail}
              sx={{
                background: 'linear-gradient(145deg, #007bff 30%, #0056b3 90%)',
                color: 'white',
                px: { xs: 3, md: 5 },
                py: { xs:0.5, md: 1 },
                fontSize: { xs: '0.9rem', md: '1.2rem' },
                borderRadius: '12px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
                textTransform: 'none',
                letterSpacing: '0.5px',
                minWidth: { xs: '120px', md: '150px' },
                '&:hover': {
                  background: 'linear-gradient(145deg, #0056b3 30%, #007bff 90%)',
                  transform: 'scale(1.05)',
                  boxShadow: '0 6px 8px rgba(0,0,0,0.3)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '@media (max-width: 600px)': {
                  px: 2,
                  py: 0.5,
                  fontSize: '0.85rem',
                  minWidth: '120px'
                }
              }}
            >
              📧 Email
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: '20px',
            background: 'linear-gradient(to bottom, #ffffff, #f8f9fa)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            border: '1px solid rgba(241, 90, 36, 0.2)',
          }
        }}
      >
        <DialogTitle sx={{
          background: '#444',
          color: 'white',
          textAlign: 'center',
          py: 2,
          fontSize: { xs: '1rem', md: '1.5rem' },
          fontWeight: 'bold',
          borderTopLeftRadius: '20px',
          borderTopRightRadius: '20px'
        }}>
          Edit Exchange Rate
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Exchange Rate"
            type="number"
            fullWidth
            variant="outlined"
            value={newRate}
            onChange={(e) => setNewRate(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#444', borderWidth: '2px', borderRadius: '15px' },
                '&:hover fieldset': { borderColor: '#e52e71' },
                '&.Mui-focused fieldset': { borderColor: '#F15A24', boxShadow: '0 0 0 4px rgba(241, 90, 36, 0.1)' }
              },
              '& .MuiInputLabel-root': { color: '#444', '&.Mui-focused': { color: '#F15A24' } },
              mt: 2
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2, justifyContent: 'center' }}>
          <Button
            onClick={handleClose}
            sx={{
              width: { xs: '100px', md: '120px' },
              height: { xs: '40px', md: '50px' },
              borderRadius: '25px',
              color: '#F15A24',
              border: '2px solid #F15A24',
              fontWeight: 'bold',
              fontSize: { xs: '0.8rem', md: '1rem' },
              '&:hover': { background: 'rgba(241, 90, 36, 0.1)', transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(241, 90, 36, 0.2)' },
              transition: 'all 0.2s ease'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            sx={{
              width: { xs: '100px', md: '120px' },
              height: { xs: '40px', md: '50px' },
              borderRadius: '25px',
              background: 'linear-gradient(45deg, #F15A24, #e52e71)',
              color: 'white',
              fontWeight: 'bold',
              fontSize: { xs: '0.8rem', md: '1rem' },
              boxShadow: '0 4px 15px rgba(241, 90, 36, 0.3)',
              '&:hover': { background: 'linear-gradient(45deg, #e52e71, #F15A24)', transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(241, 90, 36, 0.4)' },
              transition: 'all 0.2s ease'
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default React.memo(CarQuotation);